import { snack } from '@core/services/ui';
import BaseModel from '@core/model';
import ErrorService from '@core/services/ErrorService';

import OrganisationTypeEnum from '@constants/enums/organisation/type';
import PreferenceTypeEnum from '@constants/enums/preference/type';
import MarketingConsentEnum from '@constants/enums/user/marketingConsent';
import UserStatusEnum from '@constants/enums/user/status';

import UserApi from '@packages/users/api';

import i18n from '@plugins/i18n';

import { Organisation, UserRole, PhoneNumber, Terms, Investor } from '@/models';

export class User extends BaseModel {
    static name = 'User';
    static entity = 'users';
    static Api = UserApi;

    static fields() {
        return {
            ...super.fields(),
            //
            $verifyingEmail: this.boolean(false).nullable(),
            $verificationEmailSent: this.boolean(false).nullable(),
            $verificationEmailError: this.string(null).nullable(),
            //
            id: this.string(null).nullable(),
            status: this.enum(UserStatusEnum).nullable(),
            gi_ref: this.string(null).nullable(),
            title: this.string(null).nullable(),
            name: this.string(null).nullable(),
            email: this.string(null).nullable(),
            email_verified: this.boolean(null).nullable(),
            dob: this.string(null).nullable(),
            job_title: this.string(null).nullable(),
            national_insurance: this.string(null).nullable(),
            bio: this.string(null).nullable(),
            fca_number: this.string(null).nullable(),
            primary_contact: this.boolean(false).nullable(),
            visibility_groups_contact_count: this.number(null).nullable(),
            visibility_groups_grantee_count: this.number(null).nullable(),
            visibility_groups_investor_count: this.number(null).nullable(),
            nationality: this.attr(null).nullable(),
            domiciled: this.attr(null).nullable(),
            tax_residency: this.attr(null).nullable(),
            tax_cert_destination: this.string(null).nullable(),
            us_person: this.boolean(null).nullable(),
            politically_exposed: this.boolean(null).nullable(),
            can_login: this.boolean(null).nullable(),
            phone_numbers: this.modelList(PhoneNumber).nullable(),
            consent_marketing: this.enum(MarketingConsentEnum).nullable(),
            consent_marketing_date: this.string(null).nullable(),
            organisation_id: this.string(null).nullable(),
            roles: this.modelList(UserRole).nullable(),
            linked_users: this.attr(null).nullable(),
            organisations: this.attr(null).nullable(),
            signout_url: this.string(null).nullable(),
            emails: this.attr(null).nullable(),
            last_logged_in_at: this.string(null).nullable(),
            //
            organisation_type: this.enum(OrganisationTypeEnum).nullable(),
            organisation: this.belongsTo(Organisation, 'organisation_id')
        };
    }

    $filterPayload(payload) {
        payload = super.$filterPayload(payload);

        if (!payload || typeof payload !== 'object' || !Array.isArray(payload.roles)) {
            return payload;
        }

        payload.roles = payload.roles.map(entry => {
            if (!entry || typeof entry !== 'object') {
                return entry;
            }

            if (!Array.isArray(entry.roles)) {
                return entry;
            }

            return { ...entry, roles: entry.roles.slice(0, 1) };
        });

        return payload;
    }

    async $listRoles() {
        if (!this.id) {
            throw new ReferenceError('Missing ID required while attempting to list user roles');
        }

        return (await User.api.listRoles(this.id)).data;
    }

    async $verifyEmail(email) {
        if (!this.id) {
            throw new ReferenceError('Missing ID required while attempting to send verification request');
        }

        this.$verificationEmailSent = false;
        this.$verificationEmailError = null;
        this.$verifyingEmail = true;

        // Wait 2 seconds to prevent spamming the API
        await new Promise(resolve => setTimeout(resolve, 2000));

        try {
            await User.api.verifyEmail(this.id, email);

            snack({
                timeout: 20000,
                type: 'success',
                text: i18n.t('check_your_email_to_verify_your_email_address')
            });

            this.$verificationEmailSent = true;
        } catch (error) {
            let message = error.response?.data?.message || error.message;

            if (error.code >= 500) {
                message = i18n.t('an_error_occurred_while_sending_the_verification_email');
            }

            snack({
                timeout: 20000,
                type: 'error',
                text: message
            });

            this.$verificationEmailError = message;
        }

        await User.api.get(this.id);

        this.$verifyingEmail = false;
        return User.find(this.id);
    }

    async $setupMfa() {
        if (!this.id) {
            throw new ReferenceError('Missing ID required while attempting to setup MFA');
        }

        try {
            this.$saving = ['mfa'];
            await User.api.setupMfa(this.id);
            snack({
                type: 'success',
                text: `Check the inbox of ${this.email} to set up Multi-Factor Authentication`
            });
            await User.api.get(this.id);
        } catch (error) {
            ErrorService.onError(error, () => this.$setupMfa());
        } finally {
            this.$saving = [];
        }

        return User.find(this.id);
    }

    async $getTermsConditions() {
        if (!this.id) {
            throw new ReferenceError('Missing ID required while attempting to fetch terms and conditions');
        }

        const terms = (await User.api.getTerms(this.id)).data;

        return terms.map(t => (t instanceof BaseModel ? t : new Terms(t)));
    }

    async $acceptTermsConditions() {
        if (!this.id) {
            throw new ReferenceError('Missing ID required while attempting to accept terms and conditions');
        }

        return await User.api.acceptTerms(this.id);
    }

    async $getPreferences(type = null, params = {}) {
        if (!this.id) {
            throw new ReferenceError('Missing ID required while attempting to fetch preferences');
        }

        if (type && !PreferenceTypeEnum[`${type}`]) {
            throw new ReferenceError(`Invalid preference type: ${type}`);
        }

        return (await User.api.getPreferences(this.id, type, params)).data;
    }

    async $getNotificationPreferences(params = {}) {
        return await this.$getPreferences(PreferenceTypeEnum.NOTIFICATIONS, params);
    }

    async $updatePreferences(type, preferences, params = {}) {
        if (!this.id) {
            throw new ReferenceError('Missing ID required while attempting to update preferences');
        }

        if (type && !PreferenceTypeEnum[`${type}`]) {
            throw new ReferenceError(`Invalid preference type: ${type}`);
        }

        return (await User.api.updatePreferences(this.id, type, preferences, params)).data;
    }

    async $updateNotificationPreferences(preferences, params = {}) {
        return await this.$updatePreferences(PreferenceTypeEnum.NOTIFICATIONS, preferences, params);
    }

    async $getEmails(organisationId = null) {
        if (!this.id) {
            throw new ReferenceError('Missing ID required while attempting to fetch emails');
        }

        return (await User.api.getEmails(this.id, organisationId)).data;
    }

    get has_role_in_adviser() {
        const roles = Array.isArray(this.roles) ? this.roles : [];

        return roles.some(role => {
            const organisationTypes = [
                role?.organisation_type,
                role?.type,
                role?.organisation?.organisation_type,
                role?.organisation?.type
            ]
                .filter(Boolean)
                .map(type => `${type}`.trim().toUpperCase());

            return organisationTypes.includes('ADVISER') || organisationTypes.includes('GROWTH_INVEST');
        });
    }

    get is_client_of_logged_in_adviser() {
        const auth = this.Auth();

        if (!auth.is_adviser) {
            return false;
        }

        // Users cannot trigger MFA enrolment for users in the same organisation.
        if (!this.organisation_id || this.organisation_id === auth.organisation_id) {
            return false;
        }

        // This permission is limited to client (investor) organisations.
        if (this.organisation_type && this.organisation_type !== 'INVESTOR') {
            return false;
        }

        const investor = Investor.find(this.organisation_id);

        if (!investor || !Array.isArray(investor.adviser_accounts)) {
            return false;
        }

        return investor.adviser_accounts.some(aa => (aa.adviser?.id || aa.adviser_id) === auth.organisation_id);
    }

    get can_trigger_mfa_setup_validation() {
        const auth = this.Auth();
        return {
            authorised:
                auth.is_gi ||
                auth.is_superuser ||
                (auth.is_investor && auth.is_owner) ||
                this.is_client_of_logged_in_adviser,
            status_valid: ![null, UserStatusEnum.SUSPENDED, UserStatusEnum.INACTIVE].includes(this.status),
            email_verified: this.email && this.email_verified
        };
    }

    get can_trigger_mfa_setup() {
        return Object.keys(this.can_trigger_mfa_setup_validation).every(
            k => this.can_trigger_mfa_setup_validation[`${k}`]
        );
    }

    get mfa_setup_tooltip() {
        if (this.can_trigger_mfa_setup) {
            return null;
        }

        let tooltip = [i18n.t('mfa_cant_be_setup')];

        if (!this.can_trigger_mfa_setup_validation.authorised) {
            tooltip.push(i18n.t('mfa_setup_not_authorised'));
        }
        if (!this.can_trigger_mfa_setup_validation.status_valid) {
            tooltip.push(i18n.t('mfa_setup_invalid_status'));
        }
        if (!this.can_trigger_mfa_setup_validation.email_verified) {
            tooltip.push(i18n.t('mfa_setup_email_not_verified'));
        }

        return tooltip;
    }
}

export default User;
